import type { NavGuard } from '../generated/nav-tree'
import type { ModuleRoute, RouteGuard } from '../module-contract'
import { canAccess, type AccessContext } from './access'

/**
 * Whether a sidebar item is offered — FRONTEND.md §9.224, FB-370.
 *
 * **The sidebar and the router read different data about the same page.** The router reads
 * `ModuleRoute.guard`, which FB-001 corrected to mirror what the server enforces. The sidebar reads
 * the guard its item was generated with, which for the 55 items out of `manager-nav.json` is
 * *legacy's* nav guard. Where those disagree the sidebar offers a link that ends in `RouteGate`'s
 * lock panel — five of them on the owner's real `role_permissions`.
 *
 * **Both guards apply, and neither replaces the other.** The route guard carries the role gate the
 * nav guard lacks; the nav guard carries the `franchiseModule` tier gate the route guard usually
 * lacks — `/settings-backups` is gated on the `backups` module in the nav and on nothing in the
 * route. Taking the route's guard alone would offer a franchise a page outside its package, which is
 * the same class of bug pointing the other way.
 */

/** The guard the router will apply at each path. */
export function routeGuardsByPath(routes: readonly ModuleRoute[]): Map<string, RouteGuard> {
  const guards = new Map<string, RouteGuard>()
  for (const route of routes) {
    if (route.guard) guards.set(route.path, route.guard)
  }
  return guards
}

/**
 * An item is shown only when the sidebar's own gate **and** the router's gate both admit the user.
 *
 * A path with no route of its own — a section heading's target, an external link — keeps its nav
 * guard alone, because there is no second opinion to take. This is deliberately not a fallback to
 * *deny*: hiding an item because no route matched would blank parts of the sidebar the moment a path
 * were spelled differently in the two files.
 */
export function navItemVisible(
  item: { path: string; guard?: NavGuard | RouteGuard },
  routeGuards: Map<string, RouteGuard>,
  access: AccessContext,
): boolean {
  if (!canAccess(item.guard, access)) return false

  const routeGuard = routeGuards.get(item.path)
  if (routeGuard === undefined) return true

  return canAccess(routeGuard, access)
}
